import { useMutation } from "@tanstack/react-query";
import { PrimaryButton } from "components/ui/primary-button";
import { SecondaryCompactButton } from "components/ui/secondary-button";
import { TextArea } from "components/ui/textarea";
import React, { useCallback, useEffect, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import { useRegionId } from "store/selectors/hooks";
import { trpc } from "../rpc/trpc";

interface ChatMessage {
    role: "user" | "assistant";
    content: string;
}

interface SpeechRecognitionEvent {
    results: { [index: number]: { [index: number]: { transcript: string } } };
}

interface SpeechRecognitionInstance {
    continuous: boolean;
    interimResults: boolean;
    lang: string;
    onresult: ((event: SpeechRecognitionEvent) => void) | null;
    onend: (() => void) | null;
    onerror: (() => void) | null;
    start: () => void;
    stop: () => void;
    abort: () => void;
}

const getSpeechRecognitionConstructor = ():
    | (new () => SpeechRecognitionInstance)
    | null => {
    if (typeof window === "undefined") return null;
    const w = window as unknown as Record<string, unknown>;
    return (w.SpeechRecognition ?? w.webkitSpeechRecognition) as
        | (new () => SpeechRecognitionInstance)
        | null;
};

export const AgentChatPanel: React.FC = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [messages, setMessages] = useState<ChatMessage[]>([]);
    const [inputValue, setInputValue] = useState("");
    const [conversationId, setConversationId] = useState<string | null>(null);
    const [isListening, setIsListening] = useState(false);
    const messagesEndRef = useRef<HTMLDivElement>(null);
    const inputContainerRef = useRef<HTMLDivElement>(null);
    const recognitionRef = useRef<SpeechRecognitionInstance | null>(null);
    const trpcClient = trpc.useTRPC();
    const regionId = useRegionId();

    const speechSupported = getSpeechRecognitionConstructor() !== null;

    const sendMessageMutation = useMutation({
        ...trpcClient.agent.sendMessage.mutationOptions(),
        onSuccess: data => {
            setConversationId(data.conversationId);
            setMessages(prev => [...prev, { role: "assistant", content: data.response }]);
        },
        onError: () => {
            setMessages(prev => [
                ...prev,
                {
                    role: "assistant",
                    content: "Sorry, something went wrong. Please try again.",
                },
            ]);
        },
    });

    const handleSend = useCallback(() => {
        const trimmed = inputValue.trim();
        if (!trimmed || sendMessageMutation.isPending || !regionId) {
            return;
        }

        setMessages(prev => [...prev, { role: "user", content: trimmed }]);
        setInputValue("");

        sendMessageMutation.mutate({
            conversationId,
            message: trimmed,
            regionId,
        });
    }, [inputValue, conversationId, sendMessageMutation, regionId]);

    const handleKeyDown = useCallback(
        (e: React.KeyboardEvent) => {
            if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                handleSend();
            }
        },
        [handleSend],
    );

    const handleReset = useCallback(() => {
        setMessages([]);
        setConversationId(null);
    }, []);

    const handleToggle = useCallback(() => {
        setIsOpen(prev => !prev);
    }, []);

    const handleVoiceToggle = useCallback(() => {
        if (isListening && recognitionRef.current) {
            recognitionRef.current.stop();
            setIsListening(false);
            return;
        }

        const SpeechRecognitionClass = getSpeechRecognitionConstructor();
        if (!SpeechRecognitionClass) return;

        const recognition = new SpeechRecognitionClass();
        recognition.continuous = false;
        recognition.interimResults = false;
        recognition.lang = "en-US";

        recognition.onresult = (event: SpeechRecognitionEvent) => {
            const transcript = event.results[0][0].transcript;
            setInputValue(prev => (prev ? prev + " " + transcript : transcript));
        };

        recognition.onend = () => setIsListening(false);
        recognition.onerror = () => setIsListening(false);

        recognitionRef.current = recognition;
        recognition.start();
        setIsListening(true);
    }, [isListening]);

    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [messages]);

    useEffect(() => {
        if (isOpen) {
            const textarea = inputContainerRef.current?.querySelector("textarea");
            textarea?.focus();
        }
    }, [isOpen]);

    useEffect(() => {
        if (!isOpen && recognitionRef.current) {
            recognitionRef.current.abort();
            setIsListening(false);
        }
    }, [isOpen]);

    return (
        <>
            {!isOpen && (
                <div className="fixed bottom-10 right-30 z-50">
                    <PrimaryButton
                        onClick={handleToggle}
                        aria-label="Open chat"
                        className="rounded-full w-14 h-14 flex items-center justify-center shadow-lg">
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 24 24"
                            fill="currentColor"
                            className="w-6 h-6">
                            <path
                                fillRule="evenodd"
                                d="M4.848 2.771A49.144 49.144 0 0 1 12 2.25c2.43 0 4.817.178 7.152.52 1.978.292 3.348 2.024 3.348 3.97v6.02c0 1.946-1.37 3.678-3.348 3.97a48.901 48.901 0 0 1-3.476.383.39.39 0 0 0-.297.17l-2.755 4.133a.75.75 0 0 1-1.248 0l-2.755-4.133a.39.39 0 0 0-.297-.17 48.9 48.9 0 0 1-3.476-.384c-1.978-.29-3.348-2.024-3.348-3.97V6.741c0-1.946 1.37-3.68 3.348-3.97Z"
                                clipRule="evenodd"
                            />
                        </svg>
                    </PrimaryButton>
                </div>
            )}

            {isOpen && (
                <div className="fixed bottom-10 right-30 z-50 w-96 max-w-[calc(100vw-2rem)] h-[32rem] flex flex-col bg-white rounded-lg shadow-lg ring-1 ring-zinc-950/5">
                    <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
                        <span className="font-medium text-sm text-gray-900">
                            AI Assistant
                        </span>
                        <div className="flex items-center gap-2">
                            {messages.length > 0 && (
                                <SecondaryCompactButton onClick={handleReset}>
                                    New chat
                                </SecondaryCompactButton>
                            )}
                            <SecondaryCompactButton
                                onClick={handleToggle}
                                aria-label="Close chat">
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    viewBox="0 0 20 20"
                                    fill="currentColor"
                                    className="w-4 h-4">
                                    <path d="M6.28 5.22a.75.75 0 0 0-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 1 0 1.06 1.06L10 11.06l3.72 3.72a.75.75 0 1 0 1.06-1.06L11.06 10l3.72-3.72a.75.75 0 0 0-1.06-1.06L10 8.94 6.28 5.22Z" />
                                </svg>
                            </SecondaryCompactButton>
                        </div>
                    </div>

                    <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
                        {messages.length === 0 && (
                            <div className="text-center text-gray-400 text-sm mt-8">
                                <p>Ask me anything about your CRM data.</p>
                                <p className="mt-1">
                                    I can search clients, look up community details, and
                                    check your tasks.
                                </p>
                                {speechSupported && (
                                    <p className="mt-1">
                                        Tap the mic to use voice input.
                                    </p>
                                )}
                            </div>
                        )}
                        {messages.map((msg, i) => (
                            <div
                                key={i}
                                className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}>
                                <div
                                    className={`max-w-[80%] rounded-lg px-3 py-2 text-sm ${
                                        msg.role === "user"
                                            ? "bg-brand-600 text-white"
                                            : "bg-gray-100 text-gray-900"
                                    }`}>
                                    {msg.role === "assistant" ? (
                                        <div className="prose prose-sm max-w-none prose-p:my-1 prose-ul:my-1 prose-ol:my-1 prose-li:my-0.5 prose-headings:my-1.5">
                                            <ReactMarkdown>{msg.content}</ReactMarkdown>
                                        </div>
                                    ) : (
                                        <span className="whitespace-pre-wrap">
                                            {msg.content}
                                        </span>
                                    )}
                                </div>
                            </div>
                        ))}
                        {sendMessageMutation.isPending && (
                            <div className="flex justify-start">
                                <div className="bg-gray-100 rounded-lg px-3 py-2 text-sm text-gray-500">
                                    Thinking...
                                </div>
                            </div>
                        )}
                        <div ref={messagesEndRef} />
                    </div>

                    <div className="border-t border-gray-200 px-4 py-3">
                        <div ref={inputContainerRef} className="flex gap-2">
                            <TextArea
                                value={inputValue}
                                onChange={e => setInputValue(e.target.value)}
                                onKeyDown={handleKeyDown}
                                placeholder={
                                    speechSupported
                                        ? "Type or tap the mic..."
                                        : "Type a message..."
                                }
                                rows={1}
                                textSize="sm"
                                className="flex-1"
                                style={{ resize: "none" }}
                                disabled={sendMessageMutation.isPending}
                            />
                            {speechSupported && (
                                <SecondaryCompactButton
                                    onClick={handleVoiceToggle}
                                    aria-label="Voice input"
                                    className={
                                        isListening
                                            ? "text-red-600 animate-pulse"
                                            : "text-gray-400 hover:text-gray-600"
                                    }
                                    disabled={sendMessageMutation.isPending}>
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        viewBox="0 0 24 24"
                                        fill="currentColor"
                                        className="w-5 h-5">
                                        <path d="M8.25 4.5a3.75 3.75 0 1 1 7.5 0v8.25a3.75 3.75 0 1 1-7.5 0V4.5Z" />
                                        <path d="M6 10.5a.75.75 0 0 1 .75.75v1.5a5.25 5.25 0 1 0 10.5 0v-1.5a.75.75 0 0 1 1.5 0v1.5a6.751 6.751 0 0 1-6 6.709v2.291h3a.75.75 0 0 1 0 1.5h-7.5a.75.75 0 0 1 0-1.5h3v-2.291a6.751 6.751 0 0 1-6-6.709v-1.5A.75.75 0 0 1 6 10.5Z" />
                                    </svg>
                                </SecondaryCompactButton>
                            )}
                            <PrimaryButton
                                onClick={handleSend}
                                disabled={
                                    !inputValue.trim() || sendMessageMutation.isPending
                                }
                                className="px-3 py-1.5 text-sm">
                                Send
                            </PrimaryButton>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};
